import { Injectable, inject } from '@angular/core';

import {
  DeleteStoreKey,
  DeleteStoreKeyType,
  SetStoreEntry,
  SetStoreKeyType,
  StoreEntry,
  StoreKey,
  StoreKeyDefault,
  StoreKeyType,
} from '@plugins/middleware-api';
import { ScriptService } from '@plugins/script';
import type {
  CodeIngressResponse,
  Nullish,
  Override,
  PlatformFeatures,
  PlatformPermission,
  SdkDetails,
  ShareExternalContent,
  UserToken,
} from '@plugins/shared/types';

import type {
  Language,
  MiddlewareSDK,
  MiddlewareStore,
} from './middleware-sdk.types';

declare global {
  interface Window {
    eepSDK: new (cfg: unknown, jwt?: string) => MiddlewareSDK;
    __middlewareSDK: MiddlewareSDK;
  }
}

@Injectable({ providedIn: 'root' })
export class MiddlewareSDKProxy implements MiddlewareSDK {
  private readonly script = inject(ScriptService);
  private _sdk!: Promise<MiddlewareSDK>;
  public store: MiddlewareStore;

  private resolveSdk: (sdk: MiddlewareSDK) => void | undefined;

  constructor() {
    this._sdk = new Promise<MiddlewareSDK>((resolve) => {
      this.resolveSdk = resolve;

      // Below code block can be removed after codeIngress set to true for all plugins
      const jwtIngress = new URL(window.location.href).searchParams.get('jwt');
      if (jwtIngress) {
        const { platform } = JSON.parse(
          decodeURIComponent(
            window.atob(
              jwtIngress.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'),
            ),
          ),
        ) as UserToken;

        this.init(jwtIngress, platform.sdk);
      }
    });
    this.store = new MiddlewareStoreProxy(this._sdk);
  }

  /**
   * Called from AuthService on codeIngress response to initialize the SDK with the provided JWT and SDK details.
   * @param codeIngress
   */
  public async load(codeIngress: CodeIngressResponse): Promise<void> {
    this.init(codeIngress.jwt, codeIngress.sdk);
  }

  private init(jwt: string, sdk: SdkDetails): void {
    this.script.loadScript(sdk.v1).subscribe(() => {
      this.resolveSdk?.(new window.eepSDK(sdk.config, jwt));
    });
  }

  async initHeightObserver(options?: {
    min?: number | undefined;
    max?: number | undefined;
    selector?: string | undefined;
    interval?: number | undefined;
  }): Promise<void> {
    return (await this._sdk).initHeightObserver(options);
  }
  async isMobile(): Promise<boolean> {
    return (await this._sdk).isMobile();
  }
  async isIos(): Promise<boolean> {
    return (await this._sdk).isIos();
  }
  async isAndroid(): Promise<boolean> {
    return (await this._sdk).isAndroid();
  }
  async isNative(): Promise<boolean> {
    return (await this._sdk).isNative();
  }
  async openLink(url: string): Promise<void> {
    return (await this._sdk).openLink(url);
  }
  async openLinkExternal(url: string): Promise<void> {
    return (await this._sdk).openLinkExternal(url);
  }
  async openLinkInternal(url: string): Promise<void> {
    return (await this._sdk).openLinkInternal(url);
  }
  async getBrowserLanguage(): Promise<Language> {
    return (await this._sdk).getBrowserLanguage();
  }
  async getAvailableLanguages(): Promise<Language[]> {
    return (await this._sdk).getAvailableLanguages();
  }
  async getDefaultLanguage(): Promise<Language> {
    return (await this._sdk).getDefaultLanguage();
  }
  async getPreferredLanguage(options?: string[]): Promise<string> {
    return (await this._sdk).getPreferredLanguage(options);
  }
  async openNativeFileDialog(event: Event): Promise<File> {
    return (await this._sdk).openNativeFileDialog(event);
  }
  async setWindowHeight(height: number) {
    return (await this._sdk).setWindowHeight(height);
  }
  async getFeatures(): Promise<PlatformFeatures> {
    return (await this._sdk).getFeatures();
  }
  async isFeatureAvailable(feature: keyof PlatformFeatures): Promise<boolean> {
    return (await this._sdk).isFeatureAvailable(feature);
  }
  async isFeatureDisabled(feature: keyof PlatformFeatures): Promise<boolean> {
    return (await this._sdk).isFeatureDisabled(feature);
  }
  async isFeatureEnabled(feature: keyof PlatformFeatures): Promise<boolean> {
    return (await this._sdk).isFeatureEnabled(feature);
  }
  async isOverridePresent(override: Override): Promise<boolean> {
    return (await this._sdk).isOverridePresent(override);
  }
  async permissionsAvailable(perms: PlatformPermission[]): Promise<boolean> {
    return (await this._sdk).permissionsAvailable(perms);
  }
  async getUserPluginUrl(): Promise<string> {
    return (await this._sdk).getUserPluginUrl();
  }
  async getUserProfileUrl(userId: string): Promise<string | Nullish> {
    const sdk = await this._sdk;
    if (typeof sdk.getUserProfileUrl !== 'function') {
      return null;
    }
    return sdk.getUserProfileUrl(userId);
  }
  async canShareExternally(): Promise<boolean> {
    return (await this._sdk).canShareExternally();
  }
  async shareExternally(content: ShareExternalContent): Promise<boolean> {
    return (await this._sdk).shareExternally(content);
  }
  async onSave(
    cb: (respond: (data: boolean) => void) => void,
  ): Promise<{ hideControls: boolean; disconnect: () => void }> {
    return (await this._sdk).onSave(cb);
  }
}

class MiddlewareStoreProxy implements MiddlewareStore {
  constructor(private _sdk: Promise<MiddlewareSDK>) {}

  async get<T>(type: StoreKeyType, key = StoreKeyDefault): Promise<T> {
    return (await this._sdk).store.get(type, key);
  }
  async mget<T>(keys: StoreKey[]): Promise<StoreEntry<T>[]> {
    return (await this._sdk).store.mget(keys);
  }
  async set<T>(
    type: SetStoreKeyType,
    value: T,
    key = StoreKeyDefault,
  ): Promise<void> {
    return (await this._sdk).store.set(type, value, key);
  }
  async mset<T>(entries: SetStoreEntry<T>[]): Promise<void> {
    return (await this._sdk).store.mset(entries);
  }
  async delete(type: DeleteStoreKeyType, key = StoreKeyDefault): Promise<void> {
    return (await this._sdk).store.delete(type, key);
  }
  async mdelete(keys: DeleteStoreKey[]): Promise<void> {
    return (await this._sdk).store.mdelete(keys);
  }
  async list<T>(type: StoreKeyType[]): Promise<StoreEntry<T>[]> {
    return (await this._sdk).store.list(type);
  }
}
